import type { Question } from "@shared/schema";

// The display letter for an answer option at a given index (0 -> "A",
// 1 -> "B", …). Supports variable-length option lists past the original 4.
export function answerLabel(index: number): string {
  return String.fromCharCode(65 + index);
}

// True when the stem (trimmed) ends with a sentence-closing punctuation
// mark — used to decide whether the answer choices should be capitalized
// as sentence fragments rather than left as the AI emitted them.
const stemEndsWithSentencePunctuation = (stem: string): boolean => {
  const trimmed = (stem || "").trim();
  if (!trimmed) return false;
  const last = trimmed[trimmed.length - 1];
  return last === "." || last === "?" || last === "!";
};

// Heuristic: does this answer look like math / a number rather than a
// natural-language fragment? Math answers should NOT have their first
// letter capitalized — variable case (`x` vs `X`) carries meaning, and
// "120 miles" reads worse as "120 Miles". Three signals say "leave it":
//   1. Starts with a digit or arithmetic sign (12, −5, +3, 3x, $3.50, 120 miles)
//   2. Contains an equation/inequality (=, ≤, ≥, ≠, <, >)
//   3. Has an arithmetic operator wedged between non-space chars (3x+1, a/b, 2^n)
// Anything else is treated as natural language.
const looksLikeMathOrNumber = (s: string): boolean => {
  const trimmed = (s || "").trim();
  if (!trimmed) return false;
  if (/^[-−+$]?\d/.test(trimmed)) return true;
  if (/[=≤≥≠<>]/.test(trimmed)) return true;
  if (/\S[+\-×÷*/^]\S/.test(trimmed)) return true;
  return false;
};

// Capitalize the first letter character in a string, preserving any
// leading whitespace, punctuation, or numbers. ("  three" -> "  Three",
// "42" -> "42", "the answer" -> "The answer"). No-ops on math-shaped
// answers so we don't mangle equations or coefficients.
const capitalizeFirstLetter = (s: string): string => {
  if (!s) return s;
  if (looksLikeMathOrNumber(s)) return s;
  let i = 0;
  while (i < s.length && !/[a-z]/i.test(s[i])) i++;
  if (i >= s.length) return s;
  return s.slice(0, i) + s[i].toUpperCase() + s.slice(i + 1);
};

// When the stem reads like a full sentence (ends in . ? !), answer
// choices should be capitalized as sentence fragments. Returns a new
// Question with every answer's first letter capitalized; no-ops when
// the stem doesn't end with sentence punctuation so we don't mangle
// answers for stems intended as fill-in-the-blank fragments.
// capitalizeFirstLetter additionally skips math-shaped answers — see
// looksLikeMathOrNumber.
export function normalizeAnswerCapitalization(question: Question): Question {
  if (!stemEndsWithSentencePunctuation(question.stem || "")) return question;
  return {
    ...question,
    answers: question.answers.map(capitalizeFirstLetter),
  };
}
